import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Papa from "papaparse";
import { setCSVBatchData } from "../redux/slices/contentSlice/contentForm/contentForm";
import { toast } from "react-toastify";
import { urlToFile } from "../helper/onedrive/urlToFile";
import { validateCSVHeaders } from "../helper/csvHeaders";

const useCategorizeFiles = (files, type) => {
	const [fileCategories, setFileCategories] = useState({
		videos: [],
		images: [],
		audios: [],
		pdfs: [],
		subtitles: [],
		csv: null,
	});
	const [loading, setLoading] = useState(false);

	const dispatch = useDispatch();
	const { sharepoint, access_token } = useSelector(
		(state) => state.oneDriveContent
	);
	const contentForm = useSelector((state) => state.contentForm);

	const fetchPreviewUrl = useCallback(async (url, token) => {
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
			});
			const data = await response.json();
			return data.getUrl;
		} catch (error) {
			console.error("Error while fetching preview", error);
		}
	}, []);

	const handleCSVData = useCallback(
		(metadataFile) => {
			Papa.parse(metadataFile, {
				complete: (result) => {
					console.log(contentForm, "contentForm");
					const csvValidatedData = validateCSVHeaders(
						result.data,
						contentForm?.contentType
					);
					if (!csvValidatedData?.isValid) {
						csvValidatedData?.errors?.forEach((error) => {
							toast.warning(error);
						});
					}
					dispatch(setCSVBatchData(csvValidatedData.mappedHeaders));
					setFileCategories((prev) => ({
						...prev,
						csv: result.data,
					}));
				},
				header: true,
				dynamicTyping: true,
				skipEmptyLines: true,
			});
		},
		[dispatch, contentForm?.contentType]
	);

	const determineVideoType = (file) => {
		const fileName = file.name.toLowerCase();
		const size = file.size || 0;
		const duration = file.video?.duration || 0;
		
		console.log(`Analyzing video: ${file.name}`);
		console.log(`- Size: ${(size / (1024 * 1024)).toFixed(2)} MB`);
		console.log(`- Duration: ${duration}ms (${(duration / 1000 / 60).toFixed(2)} minutes)`);
		
		// Check filename patterns first (most reliable)
		if (fileName.includes('trailer') || fileName.includes('preview') || fileName.includes('teaser')) {
			console.log(`- Detected as TRAILER (filename contains trailer/preview/teaser)`);
			return "trailer";
		}
		
		if (fileName.includes('master') || fileName.includes('main') || fileName.includes('full')) {
			console.log(`- Detected as MASTER (filename contains master/main/full)`);
			return "master";
		}
		
		// Check by size (large files are usually master content)
		// 100MB threshold - files larger than 100MB are likely master content
		if (size > 100 * 1024 * 1024) { // 100MB in bytes
			console.log(`- Detected as MASTER (large file size: ${(size / (1024 * 1024)).toFixed(2)} MB)`);
			return "master";
		}
		
		// Check by duration if available (5+ minutes = master)
		if (duration > 300000) { // 5 minutes in milliseconds
			console.log(`- Detected as MASTER (long duration: ${(duration / 1000 / 60).toFixed(2)} minutes)`);
			return "master";
		}
		
		// Default fallback - if it's a substantial file but not clearly a trailer, assume master
		if (size > 50 * 1024 * 1024) { // 50MB threshold
			console.log(`- Detected as MASTER (substantial file size: ${(size / (1024 * 1024)).toFixed(2)} MB)`);
			return "master";
		}
		
		// If all else fails, small files are likely trailers
		console.log(`- Detected as TRAILER (small file size: ${(size / (1024 * 1024)).toFixed(2)} MB)`);
		return "trailer";
	};

	const getFilesByType = useCallback(
		async (files, fileType) => {
			const filesByType = files.filter((file) =>
				file.file.mimeType.startsWith(fileType)
			);
			
			console.log(`Processing ${filesByType.length} files of type: ${fileType}`);
			
			const filePromises = filesByType.map(async (file) => {
				try {
					const getUrl = await fetchPreviewUrl(
						`${sharepoint}/drives/${file.parentReference.driveId}/items/${file.id}/preview`,
						access_token
					);

					const fileDetails = {
						name: file.name,
						downloadUrl: file["@content.downloadUrl"],
						fileId: file.id,
						parentDriveId: file.parentReference.driveId,
						getUrl,
						size: file.size,
					};

					if (fileType === "video/") {
    fileDetails.type = determineVideoType(file);
    // Only set duration if file.video exists
    if (file.video) {
        fileDetails.duration = file.video.duration;
    }
}  else if (fileType === "image/" && file.image) {
    fileDetails.type = file.image.width > file.image.height
        ? "Horizontal Poster" 
        : "Vertical Poster";
} else if (fileType === "application/octet-stream") {
						fileDetails.type = "subtitle";
					}

					return fileDetails;
				} catch (error) {
					console.error(`Error processing file ${file.name}:`, error);
					return null;
				}
			});
			
			const results = await Promise.all(filePromises);
			return results.filter(Boolean); // Remove null results
		},
		[sharepoint, access_token, fetchPreviewUrl]
	);

	const categorizeFiles = useCallback(async () => {
		if (!files || files.length === 0) {
			setFileCategories({
				videos: [],
				images: [],
				audios: [],
				pdfs: [],
				subtitles: [],
				csv: null,
			});
			return;
		}

		setLoading(true);

		try {
			let categorizedFiles = {
				videos: [],
				images: [],
				audios: [],
				pdfs: [],
				csv: null,
				subtitles: [],
			};

			if (type === "onedrive") {
				console.log("Processing OneDrive files:", files.length);
				
				// Log all files for debugging
				files.forEach((file, index) => {
					console.log(`File ${index + 1}: ${file.name} (${file.file.mimeType}) - ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
				});
				
				categorizedFiles.videos = await getFilesByType(files, "video/");
				categorizedFiles.images = await getFilesByType(files, "image/");
				categorizedFiles.audios = await getFilesByType(files, "audio/");
				categorizedFiles.pdfs = await getFilesByType(
					files,
					"application/pdf"
				);
				categorizedFiles.subtitles = await getFilesByType(
					files,
					"application/octet-stream"
				);
				
				const csvFileDetails = files.find((file) =>
					file.file.mimeType.endsWith(".ms-excel") || 
					file.file.mimeType.includes("csv") ||
					file.name.toLowerCase().endsWith(".csv")
				);
				
				if (csvFileDetails) {
					try {
						const csvFile = await urlToFile(
							csvFileDetails["@content.downloadUrl"],
							csvFileDetails.name,
							csvFileDetails.file.mimeType
						);
						handleCSVData(csvFile);
					} catch (csvError) {
						console.error("Error processing CSV file:", csvError);
					}
				}
			} else {
				// Local file handling
				categorizedFiles = {
					videos: files.filter((file) =>
						file.type.startsWith("video/")
					),
					images: files.filter((file) =>
						file.type.startsWith("image/")
					),
					audios: files.filter((file) =>
						file.type.startsWith("audio/")
					),
					pdfs: files.filter((file) =>
						file.type.startsWith("application/")
					),
				};
				
				const csvFile = files.find((file) =>
					file.name.toLowerCase().endsWith(".csv")
				);
				if (csvFile) {
					try {
						handleCSVData(csvFile);
					} catch (csvError) {
						console.error("Error processing local CSV file:", csvError);
					}
				}
			}

			// Enhanced validation and logging
			console.log("=== CATEGORIZATION RESULTS ===");
			console.log("- Videos found:", categorizedFiles.videos.length);
			console.log("- Images found:", categorizedFiles.images.length);
			console.log("- Audios found:", categorizedFiles.audios.length);
			console.log("- PDFs found:", categorizedFiles.pdfs.length);
			console.log("- Subtitles found:", categorizedFiles.subtitles.length);

			// Check for master video file
			const masterVideos = categorizedFiles.videos.filter(video => video.type === "master");
			const trailerVideos = categorizedFiles.videos.filter(video => video.type === "trailer");
			
			console.log("- Master videos:", masterVideos.length);
			console.log("- Trailer videos:", trailerVideos.length);
			
			// Log video details
			categorizedFiles.videos.forEach((video, index) => {
				console.log(`  Video ${index + 1}: ${video.name} - Type: ${video.type}, Size: ${(video.size / (1024 * 1024)).toFixed(2)} MB`);
			});

			// Show appropriate messages
			if (files.length > 0) {
				if (categorizedFiles.images.length === 0) {
					const hasImageFiles = type === "onedrive" 
						? files.some(file => file.file.mimeType.startsWith("image/"))
						: files.some(file => file.type.startsWith("image/"));
					
					if (hasImageFiles) {
						toast.error("Failed to process poster files. Please try again.");
					} else {
						toast.warning("No poster files selected. Please include at least one poster image.");
					}
				} else {
					console.log("✅ Successfully processed posters:", categorizedFiles.images.length);
					toast.success(`${categorizedFiles.images.length} poster(s) processed successfully!`);
				}

				// Check for master video
				if (categorizedFiles.videos.length > 0 && masterVideos.length === 0) {
					toast.warning("No master video file detected. Please check your file sizes and naming.");
				} else if (masterVideos.length > 0) {
					toast.success(`Master video detected: ${masterVideos[0].name}`);
				}
			}

			setFileCategories(categorizedFiles);
		} catch (error) {
			console.error("Error categorizing files:", error);
			toast.error("An error occurred while processing files. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [files, type, getFilesByType, handleCSVData]);

	useEffect(() => {
		categorizeFiles();
	}, [categorizeFiles]);

	const clearFiles = useCallback(() => {
		setFileCategories({
			videos: [],
			images: [],
			audios: [],
			pdfs: [],
			subtitles: [],
			csv: null,
		});
	}, []);

	return useMemo(
		() => ({ ...fileCategories, loading, clearFiles }),
		[fileCategories, loading, clearFiles]
	);
};

export default useCategorizeFiles;